import { Link } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';

const tabanPortalUrl = process.env.REACT_APP_TABAN_PORTAL_URL || 'https://taban.so';

const proofPoints = [
  {
    label: 'Identity-first onboarding',
    body: 'Trials, purchases, and suite access begin with a Shimbirr profile instead of repeating customer setup in every product.',
  },
  {
    label: 'Business profile reuse',
    body: 'If the customer already completed company details in Shimbirr, Taban products can reuse them immediately.',
  },
  {
    label: 'Customer-owned consent',
    body: 'Every connected portal gets only what the customer keeps enabled, and access can be narrowed or revoked later in the app.',
  },
];

const suiteFlow = [
  'Customer arrives in the Taban Main Portal to start a free trial or buy a system.',
  'Shimbirr handles sign-in, account trust, and profile authority before the product flow continues.',
  'Business profile data can be reused automatically, or created once and synced back across the suite.',
  'The customer manages connected apps, sharing permissions, OTP access, and notifications from the Shimbirr app.',
];

const controlCards = [
  {
    title: 'Connected portals',
    body: 'See Taban Food, Taban PMS, and the Taban Main Portal from a single control surface.',
    tag: 'Visibility',
  },
  {
    title: 'Permission authority',
    body: 'Customers decide whether email, phone, address, business profile, and verification status are shared.',
    tag: 'Consent',
  },
  {
    title: 'Software OTPs',
    body: 'Generate rotating software-specific codes from the Shimbirr app for faster sign-in and approval flows.',
    tag: 'Access',
  },
  {
    title: 'Notifications inbox',
    body: 'Keep product approvals, suite alerts, and connection updates inside one identity-centered inbox.',
    tag: 'Alerts',
  },
];

const connectedProducts = [
  ['Taban Food', 'Operations, branches, and commerce tied to the same customer identity.'],
  ['Taban PMS', 'Project and business management under the same verified access layer.'],
  ['Taban Main Portal', 'Trials, purchases, subscriptions, and suite management now begin with Shimbirr.'],
];

export default function Landing() {
  return (
    <PublicLayout>
      <main className="overflow-hidden bg-[#131313] text-white">
        <section className="relative border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,204,0,0.14),_transparent_24%),radial-gradient(circle_at_right,_rgba(255,109,0,0.12),_transparent_24%),linear-gradient(180deg,_rgba(19,19,19,1),_rgba(28,27,27,0.96))]" />
        <div className="absolute left-1/2 top-16 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full border border-[#ffcc00]/10 bg-[#ffcc00]/5 blur-3xl" />

        <div className="relative mx-auto max-w-7xl px-6 py-16 lg:px-8 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-[1.08fr_0.92fr] lg:items-center">
            <div>
              <span className="inline-flex items-center gap-3 rounded-full border border-[#ffcc00]/25 bg-[#ffcc00]/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.34em] text-[#ffe8a8]">
                Shimbirr Customer Identity Platform
              </span>

              <h1 className="mt-6 max-w-5xl text-5xl font-black tracking-[-0.05em] text-white sm:text-6xl lg:text-7xl">
                Premium suite identity for every Taban customer.
              </h1>

              <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-300 sm:text-xl">
                Shimbirr is the premium identity and customer-control layer behind the Taban ecosystem. It powers profile-first onboarding, reusable business setup, software permissions, rotating login codes, and connected-product management from one place.
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/register"
                  className="rounded-full bg-[#ffcc00] px-7 py-3 font-semibold text-[#131313] shadow-[0_24px_70px_rgba(255,204,0,0.24)] transition hover:-translate-y-0.5"
                >
                  Create Shimbirr account
                </Link>
                <a
                  href={tabanPortalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full border border-[#ff6d00]/35 bg-[#ff6d00]/10 px-7 py-3 font-semibold text-[#ffd4b7] transition hover:bg-[#ff6d00]/15"
                >
                  Open Taban Main Portal
                </a>
                <Link
                  to="/download"
                  className="rounded-full border border-white/15 px-7 py-3 font-semibold text-white transition hover:border-white/30 hover:bg-white/5"
                >
                  Download Android APK
                </Link>
                <Link
                  to="/login"
                  className="rounded-full border border-[#ffcc00]/20 bg-white/5 px-7 py-3 font-semibold text-[#fff5d9] transition hover:border-[#ffcc00]/35 hover:bg-white/10"
                >
                  Sign in
                </Link>
              </div>

              <div className="mt-10 grid gap-4 sm:grid-cols-3">
                <HeroMetric label="Profile-first buying" value="Required" />
                <HeroMetric label="Business sync" value="Cross-suite" />
                <HeroMetric label="User authority" value="Always on" />
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-8 rounded-[2.6rem] bg-gradient-to-b from-amber-300/12 via-transparent to-yellow-300/12 blur-3xl" />
              <div className="relative overflow-hidden rounded-[2.4rem] border border-[#ffcc00]/12 bg-[#1c1b1b]/96 p-6 shadow-[0_24px_90px_rgba(0,0,0,0.35)] backdrop-blur-xl">
                <div className="flex items-center justify-between border-b border-white/10 pb-5">
                  <div>
                    <p className="text-xs uppercase tracking-[0.28em] text-[#9f9376]">Customer Suite Control</p>
                    <p className="mt-2 text-2xl font-bold text-white">Taban starts with Shimbirr</p>
                  </div>
                  <div className="rounded-2xl border border-[#ff6d00]/30 bg-[#ff6d00]/12 px-3 py-2 text-sm font-semibold text-[#ffd4b7]">
                    Premium flow
                  </div>
                </div>

                <div className="mt-6 grid gap-4">
                  {connectedProducts.map(([title, body], index) => (
                    <article
                      key={title}
                      className="rounded-[1.6rem] border border-[#ffcc00]/10 bg-black/30 p-5 transition hover:border-[#ffcc00]/30 hover:bg-black/40"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <p className="text-lg font-semibold text-white">{title}</p>
                        <span className="text-xs font-semibold uppercase tracking-[0.26em] text-[#ffe8a8]">
                          0{index + 1}
                        </span>
                      </div>
                      <p className="mt-3 text-sm leading-7 text-slate-300">{body}</p>
                    </article>
                  ))}
                </div>

                <div className="mt-6 grid gap-3 rounded-[1.8rem] border border-[#ffcc00]/20 bg-[#ffcc00]/10 p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#ffe8a8]">Customer authority</p>
                  <p className="text-sm leading-7 text-[#fff5d9]/90">
                    Customers can create or reuse business identity, narrow optional scopes during consent, disconnect software later, and manage what each portal can keep reading from Shimbirr.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-3">
          {proofPoints.map((item) => (
            <article
              key={item.label}
              className="rounded-[2rem] border border-[#ffcc00]/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(0,0,0,0.1))] p-7 shadow-[0_18px_50px_rgba(0,0,0,0.18)]"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#ffe8a8]">{item.label}</p>
              <p className="mt-4 text-sm leading-7 text-slate-300">{item.body}</p>
            </article>
          ))}
        </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 pb-16 lg:px-8">
        <div className="grid gap-8 rounded-[2.3rem] border border-[#ffcc00]/12 bg-[linear-gradient(135deg,#1c1b1b,#131313)] p-8 lg:grid-cols-[0.95fr_1.05fr] lg:p-10">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#ffe8a8]">How customers move</p>
            <h2 className="mt-4 text-3xl font-bold text-white">Trials, purchases, and suite setup without duplicated identity work.</h2>
            <p className="mt-4 text-sm leading-7 text-slate-300">
              Shimbirr cleans up customer onboarding for the whole Taban suite: identity first, business profile once, permissions under customer control, and software access managed inside the mobile experience.
            </p>
          </div>

          <div className="space-y-4">
            {suiteFlow.map((step, index) => (
              <div key={step} className="flex gap-4 rounded-[1.6rem] border border-[#ffcc00]/10 bg-white/[0.03] p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#ffcc00] font-bold text-[#131313]">
                  {index + 1}
                </div>
                <p className="text-sm leading-7 text-slate-200">{step}</p>
              </div>
            ))}
          </div>
        </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 pb-20 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-4">
          {controlCards.map((card) => (
            <article key={card.title} className="relative overflow-hidden rounded-[2rem] border border-[#ffcc00]/10 bg-[#1c1b1b] p-7">
              <div className="absolute right-0 top-0 h-24 w-24 rounded-full bg-[#ff6d00]/10 blur-2xl" />
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#ffe8a8]">{card.tag}</p>
              <h3 className="mt-4 text-2xl font-bold text-white">{card.title}</h3>
              <p className="mt-4 text-sm leading-7 text-slate-300">{card.body}</p>
            </article>
          ))}
        </div>
        </section>
      </main>
    </PublicLayout>
  );
}

function HeroMetric({ label, value }) {
  return (
    <div className="rounded-[1.5rem] border border-[#ffcc00]/10 bg-[#1c1b1b] px-5 py-4">
      <p className="text-xs uppercase tracking-[0.24em] text-[#9f9376]">{label}</p>
      <p className="mt-3 text-2xl font-bold text-white">{value}</p>
    </div>
  );
}
